// Dealt sets: reading one back, and telling two of them apart.
//
// A stored set is rendered from the tickets kept with it, never from a re-run
// at its old seed: a pull-to-refresh or the roll to tomorrow moves the board,
// and "Set 1" would quietly become a different four under the reader. The
// stored copy is the record of what was on the pad.

/**
 * Stored tickets grouped by the strategy that dealt them, in order of first
 * appearance: `[{ strategy, tickets }]`.
 *
 * `strategiesById` is the pad as it stands now. A strategy taken off the pad
 * since is named from the ticket itself — the pad cannot name it any more, and
 * the set still has to print.
 */
export function groupStoredTickets(tickets, strategiesById) {
  const groups = new Map();
  for (const t of tickets ?? []) {
    const id = t.multi?.strategyId;
    if (!groups.has(id)) {
      groups.set(id, { strategy: strategiesById?.get(id) ?? { id, name: t.multi?.label }, tickets: [] });
    }
    groups.get(id).tickets.push(t);
  }
  return [...groups.values()];
}

/**
 * A ticket as what a reader would see change: which ticket it is, and the bets
 * printed on it at the prices they were taken at.
 *
 * NOT `multi.id`, which is `${forDate}-${strategy.id}-s${seed}` — the seed is
 * in it, so every re-deal would compare as new and this would always answer
 * "different", which is the bug it exists to catch. The strategy and the tier
 * are what the ticket is called on the pad.
 */
const legPrint = (l) => `${l?.selection ?? ''}@${l?.price ?? ''}`;
const ticketPrint = (t) =>
  `${t?.multi?.strategyId ?? ''}/${t?.multi?.tier ?? ''}=${(t?.legs ?? []).map(legPrint).join('+')}`;
const dealPrint = (tickets) => (Array.isArray(tickets) ? tickets : []).map(ticketPrint).join('|');

/**
 * Every match on the board under both names a leg can carry: the engine writes
 * `matchKey`, and Classic's legs — which come from `lib/tickets.js` — only ever
 * carried the provider's event id.
 *
 * Match keys are laid down first and an event id never overwrites one, so a
 * provider id that happens to read like somebody else's match key cannot
 * quietly repoint a leg at the wrong game. `engine/picks.js` indexes the board
 * the same way, for the same reason.
 */
export function matchIndex(universe) {
  const matches = [...(universe?.matches?.values?.() ?? [])];
  const byKey = new Map();
  for (const m of matches) if (m?.matchKey) byKey.set(m.matchKey, m);
  for (const m of matches) {
    const id = m?.providerEventId;
    if (id && !byKey.has(id)) byKey.set(id, m);
  }
  return byKey;
}

/**
 * Legs stamped with the key the paper trail settles on.
 *
 * Closing rows and results are keyed by `matchKey`; a Classic leg carries the
 * provider's event id and the side it backed, and nothing else ties it to a
 * result. A leg is stamped the moment it is KEPT, because tomorrow the board
 * that could answer the question is gone. `sport` and `kickoff` come along for
 * the same reason: settling a proposal has to know which board to ask.
 *
 * Nothing already on the leg is overwritten — what was proposed is what was
 * proposed — and a leg whose match is not on the board is passed through as it
 * is rather than guessed at.
 */
export function stampLegs(legs, byKey) {
  return (legs ?? []).map((leg) => {
    const out = { ...leg };
    const match = (out.matchKey ? byKey?.get(out.matchKey) : null)
      ?? (out.eventId ? byKey?.get(out.eventId) : null)
      ?? null;
    if (!match) return out;
    if (!out.matchKey && match.matchKey) out.matchKey = match.matchKey;
    // AND THE PROVIDER'S OWN ID, which is the FIRST thing settlement joins on
    // (`rowsFor`, stores/settlement.js). The key beside it is re-derived at both
    // ends and a Classic leg's `eventId` is the provider's already; this is the
    // same fact under the name a result row uses, so a leg kept today settles
    // against a row written by a server that dates keys differently.
    if (!out.providerEventId && match.providerEventId) out.providerEventId = match.providerEventId;
    if (!out.sport && match.sport) out.sport = match.sport;
    if (!out.kickoff && match.kickoff) out.kickoff = match.kickoff;
    return out;
  });
}

/**
 * Would these two sets print the same pad?
 *
 * On a small board the next seed can deal the identical four — the tie-break
 * has nothing left to break — and "Another set" then reads as a dead button.
 * So the pad asks this before it saves anything.
 *
 * Compared on the legs and their prices, because that is what a reader would
 * see change. Not the multi's derived numbers, which all follow from the legs.
 */
export const sameDeal = (a, b) => dealPrint(a) === dealPrint(b);
